// services/authService.js
import apiClient from "../../utils/apiClient";

const API_URL = "/auth";

const toApiError = (err, fallbackMessage) => {
  const payload = err?.response?.data;
  const message =
    payload?.message ||
    payload?.error?.message ||
    payload?.error ||
    err?.message ||
    fallbackMessage;

  const normalizedError = new Error(message);
  normalizedError.status = err?.response?.status;
  normalizedError.data = payload;
  return normalizedError;
};

/**
 * SIGNUP FLOW (OTP-based)
 */

// Step 1: Send signup OTP
export const signupSendOTP = async (data) => {
  try {
    const res = await apiClient.post(`${API_URL}/signup-send-otp`, data);
    return res.data; // { success, message, masked_contact, otp_sent_to, contact }
  } catch (err) {
    throw toApiError(err, "Failed to send OTP");
  }
};

// Step 2: Verify signup OTP
export const signupVerifyOTP = async (contact, otp) => {
  try {
    const res = await apiClient.post(`${API_URL}/signup-verify-otp`, {
      contact,
      otp,
    });
    return res.data; // { success, message, user, token }
  } catch (err) {
    throw toApiError(err, "OTP verification failed");
  }
};

/**
 * LOGIN FLOW (OTP-based)
 */

// Step 1: Send login OTP
export const loginSendOTP = async (contact) => {
  try {
    const res = await apiClient.post(`${API_URL}/login-send-otp`, {
      contact,
    });
    return res.data; // { success, message, masked_contact, otp_sent_to, contact }
  } catch (err) {
    throw toApiError(err, "Failed to send OTP");
  }
};

// Step 2: Verify login OTP
export const loginVerifyOTP = async (contact, otp) => {
  try {
    const res = await apiClient.post(`${API_URL}/login-verify-otp`, {
      contact,
      otp,
    });
    return res.data; // { success, message, user, token, requiresPayment, verifiedApplications }
  } catch (err) {
    throw toApiError(err, "OTP verification failed");
  }
};

/**
 * PAYMENT FLOW
 */

// Submit payment for applications
export const submitPayment = async (data) => {
  try {
    const res = await apiClient.post(`${API_URL}/submit-payment`, data);
    return res.data;
  } catch (err) {
    throw toApiError(err, "Payment submission failed");
  }
};

/**
 * PROFILE MANAGEMENT
 */

// Update profile
export const updateProfile = async (userId, data) => {
  try {
    const res = await apiClient.put(`${API_URL}/update-profile/${userId}`, data);
    return res.data;
  } catch (err) {
    throw toApiError(err, "Update profile failed");
  }
};

/**
 * PAYMENT SETTINGS
 */

// Get active payment setting
export const getActivePaymentSetting = async () => {
  try {
    const res = await apiClient.get(`${API_URL}/active-payment-setting`);
    return res.data;
  } catch (err) {
    throw toApiError(err, "Failed to fetch payment setting");
  }
};

/**
 * LEGACY EXPORTS (Deprecated - for backward compatibility)
 * Use new OTP-based methods above instead
 */

export const signup = async (data) => {
  console.warn("signup() is deprecated. Use signupSendOTP() and signupVerifyOTP() instead.");
  return signupSendOTP(data);
};

export const login = async (data) => {
  console.warn("login() is deprecated. Use loginSendOTP() and loginVerifyOTP() instead.");
  const contact = data.email || data.contact;
  return loginSendOTP(contact);
};

export const forgotPassword = async (email) => {
  console.warn("forgotPassword() is removed. Use loginSendOTP() instead for OTP-based login.");
  throw new Error("Forgot password is no longer supported. Use login with OTP.");
};

export const verifyOTP = async (contact, otp) => {
  console.warn("verifyOTP() is deprecated. Use loginVerifyOTP() or signupVerifyOTP() instead.");
  return loginVerifyOTP(contact, otp);
};

export const updatePassword = async () => {
  console.warn("updatePassword() is removed. Passwords are no longer used. Use OTP for authentication.");
  throw new Error("Password-based authentication is no longer supported.");
};
